/**
 * Derived layer — law-derived master checklists (LEGAL-7, with their own
 * HITL review), ServiceProfile and ChecklistDefinition with their
 * derivedFrom provenance. Stale checklists (staleSince set) are clearly
 * flagged and link to the obligation change that triggered the staleness.
 * Regeneration itself is a separate ticket — this view only displays state.
 */

import { useEffect, useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Pencil } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI, LegalReviewAction } from "../../LegalAdministrationAPI";
import {
  ReviewStatusBadge,
  WrappedText,
  regulatedObjectName,
  qualificationRoleName,
  formatDate,
  formatDateTime,
} from "./legalDisplay";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

interface Properties {
  refreshSignal: number;
  onOpenObligation: (obligationKey: string) => void;
}

// Checklist items may carry legacy string forms OR structured forms (VDMA 24186):
// intervall = {modus, months?}, sollWert = the algebra AtomExpr {attr, cmp, value, unit}.
/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

const CMP_SYMBOL: Record<string, string> = {
  gt: ">", ge: "≥", lt: "<", le: "≤", eq: "=", ne: "≠", in: "∈",
};

export default function DerivedLayerPanel(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const openInChecklistEditor = (master: any) =>
    navigate(`/settings?panel=processManagement&tab=eventSchedules&checklistId=${String(master._id)}`);

  const [checklistDefinitions, setChecklistDefinitions] = useState<any[]>([]);
  const [masters, setMasters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openedChecklist, setOpenedChecklist] = useState<any>(null);
  const [openedMaster, setOpenedMaster] = useState<any>(null);
  const [masterReviewNote, setMasterReviewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Jede Entscheidung wird protokolliert und nur angehängt — deshalb ein
  // Ausweg vor dem Schreiben.
  const [pendingAction, setPendingAction] = useState<LegalReviewAction | null>(null);

  const formatIntervall = (iv: any): string => {
    if (!iv) return "";
    if (typeof iv === "string") return iv;
    if (iv.modus === "beiBedarf") return t("maintenance.onDemand");
    if (iv.months) return t("maintenance.everyNMonths", { count: iv.months });
    // periodisch with no resolved frequency is NOT an interval — never "periodisch"/"einmalig".
    return t("maintenance.intervalPending");
  };

  /** Messziel = the sollWert atom, or "Soll fehlt" where a reading exists but no threshold, or the receipt. */
  const formatMessziel = (item: any): string => {
    const s = item?.sollWert;
    if (typeof s === "string") return s;
    if (s && typeof s === "object") {
      // reading-present but no Soll → honest "Soll fehlt" (never fabricate a threshold).
      if (item?.sollMissing || s.cmp === "exists") return t("maintenance.sollMissing");
      const sym = CMP_SYMBOL[s.cmp] || s.cmp;
      return `${s.attr} ${sym} ${s.value ?? ""} ${s.unit ?? ""}`.replace(/\s+/g, " ").trim();
    }
    if (item?.kind === "quittung") return t("maintenance.quittung");
    return "—";
  };

  const load = async () => {
    try {
      setLoading(true);
      const [checklists, masterChecklists] = await Promise.all([
        legalAPI.getChecklistDefinitions(),
        legalAPI.getMasterChecklists(),
      ]);

      setChecklistDefinitions(checklists || []);
      setMasters(masterChecklists || []);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const reviewMaster = async (action: LegalReviewAction) => {
    setSubmitting(true);

    try {
      await legalAPI.reviewMasterChecklist({
        masterId: String(openedMaster._id),
        action,
        reviewNote: masterReviewNote.trim() || undefined,
      });
      setOpenedMaster(null);
      setMasterReviewNote("");
      await load();
      pushSuccess(t(`operatorResponsibilityPanel.masterReviewed.${action}`));
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const recurrenceLabel = (master: any): string => {
    // A depth-pending definition has no interval yet — never "einmalig".
    if (master?.depthStatus === "pending")
      return t("operatorResponsibilityPanel.intervalPending", { defaultValue: "Intervall folgt" });
    return master?.recurrence?.intervalMonths
      ? t("operatorResponsibilityPanel.recurrenceMonths", { months: master.recurrence.intervalMonths })
      : t("operatorResponsibilityPanel.oneTimeLabel");
  };

  const masterColumns: ColumnDef<any>[] = [
    {
      id: "editor",
      header: () => <span className="sr-only">{t("operatorResponsibilityPanel.openInChecklistEditor")}</span>,
      size: 52,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          title={t("operatorResponsibilityPanel.openInChecklistEditor")}
          aria-label={`${t("operatorResponsibilityPanel.openInChecklistEditor")} — ${row.original.name}`}
          onClick={(event) => {
            // keep the row click (review dialog) from firing as well
            event.stopPropagation();
            openInChecklistEditor(row.original);
          }}
        >
          <Pencil aria-hidden className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "name",
      accessorFn: (master: any) => master.name,
      header: t("operatorResponsibilityPanel.profileTitle"),
      enableSorting: false,
      cell: ({ row }) => <WrappedText text={row.original.name} maxWidth={300} fontSize={12} />,
    },
    {
      id: "derivedFrom",
      accessorFn: (master: any) => (master.derivedFrom || []).join(", "),
      header: t("operatorResponsibilityPanel.derivedFrom"),
      size: 190,
      enableSorting: false,
    },
    {
      id: "role",
      accessorFn: (master: any) => qualificationRoleName(t, master.role),
      header: t("operatorResponsibilityPanel.roleLabel"),
      size: 200,
      enableSorting: false,
    },
    {
      id: "recurrence",
      accessorFn: (master: any) => recurrenceLabel(master),
      header: t("operatorResponsibilityPanel.recurrenceLabel"),
      size: 150,
      enableSorting: false,
    },
    {
      id: "status",
      header: t("operatorResponsibilityPanel.status"),
      size: 150,
      enableSorting: false,
      cell: ({ row }) => <ReviewStatusBadge status={row.original.reviewStatus} />,
    },
    {
      id: "generatedAt",
      accessorFn: (master: any) => formatDateTime(master.generatedAt),
      header: t("operatorResponsibilityPanel.recordedAt"),
      size: 170,
      enableSorting: false,
    },
  ];

  /** Veraltet vs. aktuell — die Aussage steht als Wort, nicht nur als Farbe. */
  const staleBadge = (definition: any) =>
    definition.staleSince ? (
      <Badge variant="destructive">
        {t("operatorResponsibilityPanel.staleSince", { date: formatDate(definition.staleSince) })}
      </Badge>
    ) : (
      <span className="text-xs font-semibold text-success">
        {t("operatorResponsibilityPanel.upToDate")}
      </span>
    );

  const checklistColumns: ColumnDef<any>[] = [
    {
      id: "scope",
      accessorFn: (definition: any) =>
        definition.regulatedObjectCode
          ? regulatedObjectName(t, definition.regulatedObjectCode)
          : `DIN 276 ${definition.din276Category}`,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.scope")}</SortableHeader>
      ),
      size: 210,
    },
    {
      id: "serviceType",
      accessorFn: (definition: any) => definition.serviceType,
      header: t("operatorResponsibilityPanel.serviceType"),
      size: 140,
      enableSorting: false,
    },
    {
      id: "items",
      accessorFn: (definition: any) => (definition.items || []).length,
      header: t("operatorResponsibilityPanel.items"),
      size: 90,
      enableSorting: false,
    },
    {
      id: "derivedFrom",
      accessorFn: (definition: any) => (definition.derivedFrom || []).join(", "),
      header: t("operatorResponsibilityPanel.derivedFrom"),
      enableSorting: false,
      cell: ({ row }) => (
        <WrappedText text={(row.original.derivedFrom || []).join(", ")} maxWidth={320} />
      ),
    },
    {
      id: "freshness",
      header: t("operatorResponsibilityPanel.freshness"),
      size: 180,
      enableSorting: false,
      cell: ({ row }) => staleBadge(row.original),
    },
    {
      id: "status",
      header: t("operatorResponsibilityPanel.status"),
      size: 140,
      enableSorting: false,
      cell: ({ row }) => <ReviewStatusBadge status={row.original.reviewStatus} />,
    },
  ];

  const masterIsReviewable =
    !!openedMaster &&
    openedMaster.depthStatus !== "pending" &&
    (openedMaster.reviewStatus === "pending" || openedMaster.reviewStatus === "needsRevision");
  const noteId = `${fieldId}-master-note`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
      <Section title={t("operatorResponsibilityPanel.masterChecklists")}>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("operatorResponsibilityPanel.masterPendingHint")}
        </p>
        {loading ? (
          <div className="mt-3 flex min-w-0 flex-col gap-2">
            <Skeleton className="h-6 w-[220px]" />
            <Skeleton className="h-[180px] w-full" />
          </div>
        ) : masters.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="text-sm font-semibold">
              {t("operatorResponsibilityPanel.noMasterChecklistsTitle")}
            </p>
            <p className="max-w-prose text-sm text-muted-foreground">
              {t("operatorResponsibilityPanel.noMasterChecklists")}
            </p>
          </div>
        ) : (
          <div className="mt-3 min-w-0 overflow-x-auto">
            <DataTable
              columns={masterColumns}
              data={masters}
              pageSize={10}
              previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
              nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
              onRowClick={(master: any) => {
                setMasterReviewNote("");
                setOpenedMaster(master);
              }}
              emptyMessage={t("operatorResponsibilityPanel.noMasterChecklists")}
            />
          </div>
        )}
      </Section>

      {/* ServiceProfile section deliberately hidden — not in use at the moment. */}
      <Section title={t("operatorResponsibilityPanel.checklistDefinitions")}>
        {loading ? (
          <div className="flex min-w-0 flex-col gap-2">
            <Skeleton className="h-6 w-[220px]" />
            <Skeleton className="h-[140px] w-full" />
          </div>
        ) : checklistDefinitions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="text-sm font-semibold">
              {t("operatorResponsibilityPanel.noChecklistDefinitions")}
            </p>
            <p className="max-w-prose text-sm text-muted-foreground">
              {t("operatorResponsibilityPanel.noChecklistDefinitionsHint")}
            </p>
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={checklistColumns}
              data={checklistDefinitions}
              pageSize={10}
              previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
              nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
              onRowClick={(definition: any) => setOpenedChecklist(definition)}
              emptyMessage={t("operatorResponsibilityPanel.noChecklistDefinitions")}
            />
          </div>
        )}
      </Section>

      {/* Checklist detail viewer */}
      <Dialog
        open={!!openedChecklist}
        onOpenChange={(open) => {
          if (!open) setOpenedChecklist(null);
        }}
      >
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[840px]">
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">
              {openedChecklist
                ? `${
                    openedChecklist.regulatedObjectCode
                      ? regulatedObjectName(t, openedChecklist.regulatedObjectCode)
                      : openedChecklist.din276Category || ""
                  } · ${openedChecklist.serviceType || ""}`
                : ""}
            </DialogTitle>
            <DialogDescription className="min-w-0 break-words">
              {t("operatorResponsibilityPanel.derivedFrom")}:{" "}
              {(openedChecklist?.derivedFrom || []).join(", ") || "—"}
            </DialogDescription>
          </DialogHeader>

          {openedChecklist ? (
            <div className="flex min-w-0 flex-col gap-4">
              {openedChecklist.staleSince ? (
                <Alert variant="destructive">
                  <AlertTitle className="min-w-0 break-words">
                    {t("operatorResponsibilityPanel.staleSince", {
                      date: formatDate(openedChecklist.staleSince),
                    })}
                  </AlertTitle>
                  <AlertDescription className="flex min-w-0 flex-col gap-1">
                    {(openedChecklist.staleTriggers || []).map((trigger: any) => (
                      <span key={trigger.obligationKey} className="min-w-0 break-words">
                        {t("operatorResponsibilityPanel.staleTrigger", {
                          key: trigger.obligationKey,
                          fromVersion: trigger.referencedVersion,
                          toVersion: trigger.currentVersion,
                        })}{" "}
                        <button
                          type="button"
                          className="underline underline-offset-2 hover:no-underline"
                          onClick={() => {
                            setOpenedChecklist(null);
                            properties.onOpenObligation(trigger.obligationKey);
                          }}
                        >
                          {t("operatorResponsibilityPanel.openObligation")}
                        </button>
                      </span>
                    ))}
                  </AlertDescription>
                </Alert>
              ) : null}

              {(openedChecklist.items || []).length === 0 ? (
                <p className="min-w-0 break-words text-sm text-muted-foreground">
                  {t("operatorResponsibilityPanel.noChecklistItems")}
                </p>
              ) : (
                <div className="min-w-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("operatorResponsibilityPanel.task")}</TableHead>
                        <TableHead className="w-[130px]">
                          {t("operatorResponsibilityPanel.interval")}
                        </TableHead>
                        <TableHead className="w-[200px]">
                          {t("operatorResponsibilityPanel.measurementTarget", "Messziel")}
                        </TableHead>
                        <TableHead className="w-[200px]">
                          {t("operatorResponsibilityPanel.normReference")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(openedChecklist.items || []).map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell className="min-w-0 break-words">{item.task}</TableCell>
                          <TableCell>{formatIntervall(item.intervall)}</TableCell>
                          <TableCell className="min-w-0 break-words">{formatMessziel(item)}</TableCell>
                          <TableCell className="min-w-0 break-words">{item.normbezug}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setOpenedChecklist(null)}
            >
              {t("global.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master checklist detail + HITL review (LEGAL-7): a master stays
          pending — and unusable for tenant bindings — until confirmed here. */}
      <Dialog
        open={!!openedMaster}
        onOpenChange={(open) => {
          if (!open) setOpenedMaster(null);
        }}
      >
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[880px]">
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">{openedMaster?.name || ""}</DialogTitle>
            <DialogDescription className="min-w-0 break-words">
              {openedMaster?.description || ""}
            </DialogDescription>
          </DialogHeader>

          {openedMaster ? (
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex min-w-0 flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <ReviewStatusBadge status={openedMaster.reviewStatus} />
                <span className="min-w-0 break-words">
                  {t("operatorResponsibilityPanel.derivedFrom")}:{" "}
                  {(openedMaster.derivedFrom || []).join(", ") || "—"}
                </span>
                <span className="min-w-0 break-words">
                  {t("operatorResponsibilityPanel.roleLabel")}:{" "}
                  {qualificationRoleName(t, openedMaster.role)}
                </span>
                <span className="min-w-0 break-words">
                  {t("operatorResponsibilityPanel.recurrenceLabel")}: {recurrenceLabel(openedMaster)}
                </span>
                {openedMaster.signatureRequired ? (
                  <span className="font-semibold text-warning">
                    {t("operatorResponsibilityPanel.signatureRequired")}
                  </span>
                ) : null}
              </div>

              <div className="min-w-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("operatorResponsibilityPanel.fieldText")}</TableHead>
                      <TableHead className="w-[130px]">
                        {t("operatorResponsibilityPanel.fieldType")}
                      </TableHead>
                      <TableHead className="w-[90px]">
                        <span className="sr-only">{t("operatorResponsibilityPanel.optionalLabel")}</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(openedMaster.fields || []).map((field: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="min-w-0 break-words">
                          {field.text}
                          {field.type === "measurement" && (field.min != null || field.max != null)
                            ? ` (${field.min ?? ""}–${field.max ?? ""} ${field.unit ?? ""})`
                            : ""}
                        </TableCell>
                        <TableCell>{field.type}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {field.optional ? t("operatorResponsibilityPanel.optionalLabel") : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {openedMaster.depthStatus === "pending" ? (
                <Alert variant="warning">
                  <AlertDescription className="min-w-0 break-words">
                    {t("operatorResponsibilityPanel.depthPendingNotice", {
                      defaultValue:
                        "Tiefe ausstehend – diese Definition trägt noch keine Schritte und ist nicht bestätigbar/zeichenbar.",
                    })}
                  </AlertDescription>
                </Alert>
              ) : masterIsReviewable ? (
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={noteId}>{t("operatorResponsibilityPanel.reviewNote")}</Label>
                  <Textarea
                    id={noteId}
                    rows={3}
                    value={masterReviewNote}
                    onChange={(event) => setMasterReviewNote(event.target.value)}
                  />
                  <p className="min-w-0 break-words text-xs text-muted-foreground">
                    {t("operatorResponsibilityPanel.reviewNoteRequired")}
                  </p>
                </div>
              ) : (
                <p className="min-w-0 break-words text-sm text-muted-foreground">
                  {openedMaster.confirmedBy
                    ? t("operatorResponsibilityPanel.alreadyConfirmed", {
                        reviewer: openedMaster.confirmedBy,
                        date: formatDateTime(openedMaster.confirmedAt),
                      })
                    : null}
                </p>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setOpenedMaster(null)}
            >
              {t("global.close")}
            </Button>
            {openedMaster ? (
              <Button
                variant="outline"
                className="max-sm:w-full"
                onClick={() => openInChecklistEditor(openedMaster)}
              >
                {t("operatorResponsibilityPanel.openInChecklistEditor")}
              </Button>
            ) : null}
            {masterIsReviewable ? (
              <>
                <Button
                  variant="outline"
                  className="max-sm:w-full"
                  disabled={submitting || masterReviewNote.trim().length === 0}
                  onClick={() => setPendingAction("needsRevision")}
                >
                  {t("operatorResponsibilityPanel.needsRevision")}
                </Button>
                <Button
                  variant="outline"
                  className="max-sm:w-full"
                  disabled={submitting || masterReviewNote.trim().length === 0}
                  onClick={() => setPendingAction("reject")}
                >
                  {t("operatorResponsibilityPanel.reject")}
                </Button>
                <Button
                  className="gap-2 max-sm:w-full"
                  disabled={submitting}
                  onClick={() => setPendingAction("confirm")}
                >
                  {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                  {t("operatorResponsibilityPanel.confirm")}
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Die Entscheidung über eine Musterliste wird protokolliert und schaltet
          sie für Mandantenbindungen frei — deshalb ein Ausweg davor. */}
      <AlertDialog
        open={!!pendingAction}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction
                ? t(`operatorResponsibilityPanel.masterReviewDialogTitle.${pendingAction}`)
                : ""}
            </AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {pendingAction
                ? t(`operatorResponsibilityPanel.masterReviewDialogMessage.${pendingAction}`, {
                    name: openedMaster?.name || "",
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                const action = pendingAction;
                setPendingAction(null);
                if (action) reviewMaster(action);
              }}
            >
              {pendingAction === "confirm"
                ? t("operatorResponsibilityPanel.confirm")
                : pendingAction === "reject"
                  ? t("operatorResponsibilityPanel.reject")
                  : t("operatorResponsibilityPanel.needsRevision")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
